import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { TextField } from '@/components/text-field';
import type { Copy } from '@/lib/messages';

interface RequireBirthdateStepProps {
  copy: Copy;
  pending: boolean;
  /** Form-level error (network, throttle, …) — rendered as the top alert. */
  formError: string | null;
  /** Field-level error (under age, bad date) — rendered on the date field. */
  fieldError: string | null;
  minimumAge: number;
  onSubmit: (birthdate: string) => void;
}

/**
 * The mandatory age gate for an account that never passed it — in practice a
 * migrated one, since the ETL writes accounts directly and the sign-up gate
 * never ran (docs/auth/40-features/age-gate.md, ADR 0021).
 *
 * There is no skip: the login is already proven but held server-side, and no
 * token is issued until this is answered. A refusal is inline and the field
 * stays editable, because the likeliest cause of one is a mistyped year.
 */
export function RequireBirthdateStep({
  copy,
  pending,
  formError,
  fieldError,
  minimumAge,
  onSubmit,
}: RequireBirthdateStepProps) {
  const [birthdate, setBirthdate] = useState('');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!birthdate) return;
    onSubmit(birthdate);
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {formError ? <Alert tone="error">{formError}</Alert> : null}
      <TextField
        id="require-birthdate"
        label={copy.birthdateLabel}
        type="date"
        error={fieldError}
        value={birthdate}
        onChange={(event) => setBirthdate(event.target.value)}
        autoComplete="bday"
        max={new Date().toISOString().slice(0, 10)}
        hint={copy.birthdateHint(minimumAge)}
        required
        autoFocus
      />
      {/* Gated only on presence: the threshold is server configuration, so the
          screen cannot honestly pre-judge the answer (unlike the password
          policy, which it can). */}
      <Button type="submit" loading={pending} disabled={pending || !birthdate}>
        {copy.birthdateRequiredSubmit}
      </Button>
    </form>
  );
}
